import { useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Send, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import ConsultationScheduler from "@/components/ConsultationScheduler";

function Field({ label, name, type = "text", required = false, placeholder, value, onChange, testId, as = "input", options }) {
    const id = `field-${name}`;
    if (as === "textarea") {
        return (
            <div>
                <label htmlFor={id} className="label-vl">
                    {label}
                    {required && <span className="text-[#F4A000] ml-1">*</span>}
                </label>
                <textarea
                    id={id}
                    name={name}
                    required={required}
                    placeholder={placeholder}
                    value={value || ""}
                    onChange={onChange}
                    data-testid={testId}
                    className="input-vl"
                    rows={3}
                />
            </div>
        );
    }
    if (as === "select") {
        return (
            <div>
                <label htmlFor={id} className="label-vl">
                    {label}
                    {required && <span className="text-[#F4A000] ml-1">*</span>}
                </label>
                <select
                    id={id}
                    name={name}
                    required={required}
                    value={value || ""}
                    onChange={onChange}
                    data-testid={testId}
                    className="input-vl"
                >
                    <option value="">Select…</option>
                    {(options || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            </div>
        );
    }
    return (
        <div>
            <label htmlFor={id} className="label-vl">
                {label}
                {required && <span className="text-[#F4A000] ml-1">*</span>}
            </label>
            <input
                id={id}
                name={name}
                type={type}
                required={required}
                placeholder={placeholder}
                value={value || ""}
                onChange={onChange}
                data-testid={testId}
                className="input-vl"
            />
        </div>
    );
}

function SubmitBtn({ loading, label = "Submit Inquiry", testId }) {
    return (
        <button
            type="submit"
            disabled={loading}
            data-testid={testId}
            className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {loading ? "Submitting…" : label}
        </button>
    );
}

function useForm(initial) {
    const [data, setData] = useState(initial);
    const onChange = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));
    return [data, setData, onChange];
}

function TransportationForm() {
    const initial = { name: "", company: "", email: "", phone: "", origin: "", destination: "", material_type: "", weight: "", frequency: "", vehicle_requirement: "", message: "" };
    const [data, setData, onChange] = useForm(initial);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead("transportation", data);
            toast.success("Transportation inquiry received. Our team will respond within 24 hours.");
            setData(initial);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} data-testid="form-transportation" className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name" name="name" required value={data.name} onChange={onChange} testId="transport-name" />
            <Field label="Company" name="company" required value={data.company} onChange={onChange} testId="transport-company" />
            <Field label="Email" type="email" name="email" required value={data.email} onChange={onChange} testId="transport-email" />
            <Field label="Phone" name="phone" required value={data.phone} onChange={onChange} testId="transport-phone" />
            <Field label="Origin" name="origin" required value={data.origin} onChange={onChange} testId="transport-origin" placeholder="e.g. Chakan, Pune" />
            <Field label="Destination" name="destination" required value={data.destination} onChange={onChange} testId="transport-destination" placeholder="e.g. Aurangabad" />
            <Field label="Material Type" name="material_type" required value={data.material_type} onChange={onChange} testId="transport-material" placeholder="e.g. Auto components" />
            <Field label="Weight (in MT)" name="weight" required value={data.weight} onChange={onChange} testId="transport-weight" placeholder="e.g. 12" />
            <Field
                label="Frequency"
                as="select"
                name="frequency"
                required
                value={data.frequency}
                onChange={onChange}
                testId="transport-frequency"
                options={["Daily", "Weekly", "Fortnightly", "Monthly", "Ad-hoc"]}
            />
            <Field
                label="Vehicle Requirement"
                as="select"
                name="vehicle_requirement"
                value={data.vehicle_requirement}
                onChange={onChange}
                testId="transport-vehicle"
                options={["LCV", "MCV", "FTL 17 ft", "FTL 22 ft", "FTL 32 ft", "Trailer 20 ft", "Trailer 40 ft", "ODC"]}
            />
            <div className="md:col-span-2">
                <Field
                    label="Additional Notes"
                    as="textarea"
                    name="message"
                    value={data.message}
                    onChange={onChange}
                    testId="transport-message"
                />
            </div>
            <div className="md:col-span-2">
                <SubmitBtn loading={loading} label="Request Transportation Quote" testId="transport-submit" />
            </div>
        </form>
    );
}

function WarehousingForm() {
    const initial = { name: "", company: "", email: "", phone: "", location: "", sqft_required: "", inventory_type: "", duration: "", monthly_throughput: "", message: "" };
    const [data, setData, onChange] = useForm(initial);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead("warehousing", data);
            toast.success("Warehousing inquiry received. Our team will respond within 24 hours.");
            setData(initial);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} data-testid="form-warehousing" className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name" name="name" required value={data.name} onChange={onChange} testId="wh-name" />
            <Field label="Company" name="company" required value={data.company} onChange={onChange} testId="wh-company" />
            <Field label="Email" type="email" name="email" required value={data.email} onChange={onChange} testId="wh-email" />
            <Field label="Phone" name="phone" required value={data.phone} onChange={onChange} testId="wh-phone" />
            <Field
                label="Preferred Location"
                as="select"
                name="location"
                required
                value={data.location}
                onChange={onChange}
                testId="wh-location"
                options={["Waluj, Aurangabad", "Chakan, Pune", "Built-to-Suit (Other)", "Pan India"]}
            />
            <Field label="Sq Ft Required" name="sqft_required" required value={data.sqft_required} onChange={onChange} testId="wh-sqft" placeholder="e.g. 20,000 sqft" />
            <Field label="Inventory Type" name="inventory_type" required value={data.inventory_type} onChange={onChange} testId="wh-inv-type" placeholder="e.g. Auto components, finished goods" />
            <Field
                label="Duration"
                as="select"
                name="duration"
                required
                value={data.duration}
                onChange={onChange}
                testId="wh-duration"
                options={["3 months", "6 months", "1 year", "2-3 years", "3+ years"]}
            />
            <Field label="Expected Monthly Throughput" name="monthly_throughput" value={data.monthly_throughput} onChange={onChange} testId="wh-throughput" placeholder="e.g. 1500 MT" />
            <div className="md:col-span-2">
                <Field
                    label="Additional Notes"
                    as="textarea"
                    name="message"
                    value={data.message}
                    onChange={onChange}
                    testId="wh-message"
                />
            </div>
            <div className="md:col-span-2">
                <SubmitBtn loading={loading} label="Request Warehousing Proposal" testId="wh-submit" />
            </div>
        </form>
    );
}

function ConsultationForm() {
    const initial = { name: "", company: "", designation: "", email: "", phone: "", industry: "", current_setup: "", key_challenges: "", expected_volume: "", scheduled_slot: "", message: "" };
    const [data, setData, onChange] = useForm(initial);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead("consultation", data);
            const slotMsg = data.scheduled_slot ? " Your slot is booked." : " A logistics expert will reach out shortly.";
            toast.success("Consultation request received." + slotMsg);
            setData(initial);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} data-testid="form-consultation" className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Name" name="name" required value={data.name} onChange={onChange} testId="cons-name" />
            <Field label="Company" name="company" required value={data.company} onChange={onChange} testId="cons-company" />
            <Field label="Designation" name="designation" value={data.designation} onChange={onChange} testId="cons-designation" />
            <Field
                label="Industry"
                as="select"
                name="industry"
                required
                value={data.industry}
                onChange={onChange}
                testId="cons-industry"
                options={["Automotive OEM", "Auto Components", "Engineering", "Manufacturing", "Power & Projects", "Heavy / Agri Machinery", "Other"]}
            />
            <Field label="Email" type="email" name="email" required value={data.email} onChange={onChange} testId="cons-email" />
            <Field label="Phone" name="phone" required value={data.phone} onChange={onChange} testId="cons-phone" />
            <div className="md:col-span-2">
                <Field
                    label="Current Logistics Setup"
                    as="textarea"
                    name="current_setup"
                    value={data.current_setup}
                    onChange={onChange}
                    testId="cons-setup"
                    placeholder="In-house, multiple vendors, single 3PL, etc."
                />
            </div>
            <div className="md:col-span-2">
                <Field
                    label="Key Challenges"
                    as="textarea"
                    name="key_challenges"
                    value={data.key_challenges}
                    onChange={onChange}
                    testId="cons-challenges"
                    placeholder="e.g. cost overruns, JIT misses, inventory blind-spots"
                />
            </div>
            <Field label="Expected Volume (Monthly)" name="expected_volume" value={data.expected_volume} onChange={onChange} testId="cons-volume" placeholder="e.g. 500 MT / 20 trips" />
            <Field label="Anything else?" name="message" value={data.message} onChange={onChange} testId="cons-message" />
            <div className="md:col-span-2">
                <ConsultationScheduler
                    value={data.scheduled_slot}
                    onChange={(v) => setData((d) => ({ ...d, scheduled_slot: v }))}
                />
            </div>
            <div className="md:col-span-2">
                <SubmitBtn loading={loading} label={data.scheduled_slot ? "Confirm Booking" : "Talk to a Supply Chain Expert"} testId="cons-submit" />
            </div>
        </form>
    );
}

export default function InquiryForms({ defaultTab = "consultation", testId = "inquiry-forms" }) {
    const [tab, setTab] = useState(defaultTab);
    return (
        <div data-testid={testId} className="bg-white border border-[#CBD5E1]">
            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="w-full grid grid-cols-3 bg-[#E9EEF3] p-0 h-auto rounded-none border-b border-[#CBD5E1]">
                    <TabsTrigger
                        value="consultation"
                        data-testid="tab-consultation"
                        className="rounded-none py-3 sm:py-4 px-1 sm:px-3 font-display uppercase tracking-tight sm:tracking-wider text-[10px] sm:text-xs md:text-sm leading-tight whitespace-normal data-[state=active]:bg-[#28406A] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                        Consultation
                    </TabsTrigger>
                    <TabsTrigger
                        value="transportation"
                        data-testid="tab-transportation"
                        className="rounded-none py-3 sm:py-4 px-1 sm:px-3 font-display uppercase tracking-tight sm:tracking-wider text-[10px] sm:text-xs md:text-sm leading-tight whitespace-normal data-[state=active]:bg-[#28406A] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                        Transportation
                    </TabsTrigger>
                    <TabsTrigger
                        value="warehousing"
                        data-testid="tab-warehousing"
                        className="rounded-none py-3 sm:py-4 px-1 sm:px-3 font-display uppercase tracking-tight sm:tracking-wider text-[10px] sm:text-xs md:text-sm leading-tight whitespace-normal data-[state=active]:bg-[#28406A] data-[state=active]:text-white data-[state=active]:shadow-none"
                    >
                        Warehousing
                    </TabsTrigger>
                </TabsList>
                <TabsContent forceMount value="consultation" className={`p-4 sm:p-6 md:p-10 ${tab !== "consultation" ? "hidden" : ""}`}>
                    <ConsultationForm />
                </TabsContent>
                <TabsContent forceMount value="transportation" className={`p-4 sm:p-6 md:p-10 ${tab !== "transportation" ? "hidden" : ""}`}>
                    <TransportationForm />
                </TabsContent>
                <TabsContent forceMount value="warehousing" className={`p-4 sm:p-6 md:p-10 ${tab !== "warehousing" ? "hidden" : ""}`}>
                    <WarehousingForm />
                </TabsContent>
            </Tabs>
        </div>
    );
}
